import { useState } from "react";
import { getSessionToken, useSession } from "../../hooks/useSession.js";
import { Button, Card, CardDescription, CardTitle, Input, Label } from "../ui/index.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export function PortalProfilePanel() {
  const token = getSessionToken();
  const { agent, refresh } = useSession();
  const [firstName, setFirstName] = useState(agent?.firstName ?? "");
  const [lastName, setLastName] = useState(agent?.lastName ?? "");
  const [email, setEmail] = useState(agent?.email ?? "");
  const [npn, setNpn] = useState(agent?.npn ?? "");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function saveProfile() {
    if (!token) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/auth/me/profile`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ firstName, lastName, email, npn: npn.trim() || null }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json.error || "Unable to save profile");
      }
      setFirstName(json.data.firstName ?? "");
      setLastName(json.data.lastName ?? "");
      setEmail(json.data.email ?? "");
      setNpn(json.data.npn ?? "");
      await refresh();
      setMessage("Profile saved. Use this email the next time you sign in.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save profile");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="relative space-y-6 overflow-hidden border-2 bg-accent-muted border-accent">
      <div className="pointer-events-none absolute -right-8 -top-8 h-36 w-36 text-accent opacity-10" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="8" r="4" /><path strokeLinecap="round" d="M4 21a8 8 0 0116 0" /></svg>
      </div>
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-accent">Carrier identity</p>
        <CardTitle className="mt-2 text-2xl font-extrabold">Professional profile</CardTitle>
        <CardDescription className="mt-1">
          Keep your login email, legal name, and National Producer Number current for carrier contracting.
        </CardDescription>
      </div>
      <Label className="block max-w-xl">
        Email
        <Input
          className="mt-1.5"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <span className="mt-1 block text-xs text-content-muted">
          This is the address you sign in with and the one that appears on a contract request.
        </span>
      </Label>
      <div className="grid gap-3 sm:grid-cols-2">
        <Label>
          First name
          <Input className="mt-1.5" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
        </Label>
        <Label>
          Last name
          <Input className="mt-1.5" value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </Label>
      </div>
      <Label className="block max-w-sm">
        National Producer Number (NPN)
        <Input
          className="mt-1.5"
          inputMode="numeric"
          maxLength={10}
          value={npn}
          onChange={(event) => setNpn(event.target.value.replace(/\D/g, ""))}
          aria-describedby="npn-help"
        />
        <span id="npn-help" className="mt-1 block text-xs text-content-muted">
          4–10 digits. Leave blank only if you do not have an NPN yet.
        </span>
      </Label>
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <Button className="w-full sm:w-auto" size="lg" type="button" disabled={saving} onClick={() => void saveProfile()}>
          {saving ? "Saving…" : "Save profile"}
        </Button>
        {message && <p className="text-sm text-status-active-text">{message}</p>}
        {error && <p className="text-sm text-status-rejected-text">{error}</p>}
      </div>
    </Card>
  );
}
